/**
 * ChartBlock — Line, Bar, and Pie charts using Recharts
 *
 * Dark theme with cyan/teal/purple palette.
 * Responsive container, tooltips, and legends.
 */
import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import type { PieLabelRenderProps } from 'recharts';
import type { ChartBlockData } from '../types';

interface ChartBlockProps {
  data: ChartBlockData;
}

// Default color palette — cyan/teal/purple/amber/rose
const DEFAULT_COLORS = [
  '#22d3ee', // cyan-400
  '#2dd4bf', // teal-400
  '#a78bfa', // purple-400
  '#fbbf24', // amber-400
  '#fb7185', // rose-400
  '#34d399', // emerald-400
  '#60a5fa', // blue-400
  '#f472b6', // pink-400
];

const getColor = (idx: number, explicit?: string) =>
  explicit || DEFAULT_COLORS[idx % DEFAULT_COLORS.length];

// Custom tooltip styled for dark theme
const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: { name: string; value: number; color: string }[];
  label?: string;
}> = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-xl bg-black/90 backdrop-blur-sm border border-cyan-400/20 px-3 py-2 shadow-[0_0_15px_rgba(34,211,238,0.15)]">
      {label && (
        <p className="text-[10px] text-cyan-400/50 font-bold uppercase tracking-wider mb-1">
          {label}
        </p>
      )}
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-cyan-300/70">{entry.name}:</span>
          <span className="font-semibold text-cyan-300">
            {typeof entry.value === 'number'
              ? entry.value.toLocaleString()
              : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

// Custom legend
const CustomLegend: React.FC<{
  payload?: { value: string; color: string }[];
}> = ({ payload }) => {
  if (!payload || payload.length <= 1) return null;
  return (
    <div className="flex flex-wrap justify-center gap-4 mt-2">
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-1.5 text-xs">
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-cyan-400/60">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

export const ChartBlock: React.FC<ChartBlockProps> = ({ data }) => {
  const { chartType, title, data: chartData, xKey, series } = data;

  if (!chartData || chartData.length === 0 || !series || series.length === 0) {
    return null;
  }

  const renderChart = () => {
    if (chartType === 'pie') {
      // Pie: use first series key as value
      const valueKey = series[0].key;
      return (
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey={valueKey}
              nameKey={xKey}
              cx="50%"
              cy="50%"
              outerRadius={100}
              innerRadius={50}
              strokeWidth={1}
              stroke="rgba(34,211,238,0.15)"
              label={(props: PieLabelRenderProps) => {
                const name = String(props.name ?? '');
                const percent = Number(props.percent ?? 0);
                return `${name} ${(percent * 100).toFixed(0)}%`;
              }}
              labelLine={{ stroke: 'rgba(34,211,238,0.3)' }}
            >
              {chartData.map((_, idx) => (
                <Cell
                  key={idx}
                  fill={getColor(idx, series[idx]?.color)}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomLegend />} />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    const ChartComponent = chartType === 'bar' ? BarChart : LineChart;
    const DataComponent = chartType === 'bar' ? Bar : Line;

    return (
      <ResponsiveContainer width="100%" height={280}>
        <ChartComponent data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(34,211,238,0.08)" />
          <XAxis
            dataKey={xKey}
            tick={{ fontSize: 11, fill: 'rgba(34,211,238,0.5)' }}
            axisLine={{ stroke: 'rgba(34,211,238,0.15)' }}
            tickLine={{ stroke: 'rgba(34,211,238,0.1)' }}
          />
          <YAxis
            tick={{ fontSize: 11, fill: 'rgba(34,211,238,0.5)' }}
            axisLine={{ stroke: 'rgba(34,211,238,0.15)' }}
            tickLine={{ stroke: 'rgba(34,211,238,0.1)' }}
            width={50}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend content={<CustomLegend />} />
          {series.map((s, idx) => {
            const color = getColor(idx, s.color);
            if (chartType === 'bar') {
              return (
                <DataComponent
                  key={s.key}
                  dataKey={s.key}
                  name={s.label}
                  fill={color}
                  radius={[4, 4, 0, 0] as unknown as number}
                  opacity={0.85}
                />
              );
            }
            return (
              <DataComponent
                key={s.key}
                dataKey={s.key}
                name={s.label}
                stroke={color}
                strokeWidth={2}
                dot={{ fill: color, r: 3, strokeWidth: 0 }}
                activeDot={{ r: 5, stroke: color, strokeWidth: 2, fill: '#0A0A0F' }}
              />
            );
          })}
        </ChartComponent>
      </ResponsiveContainer>
    );
  };

  return (
    <div className="rounded-2xl bg-black/50 backdrop-blur-xl border border-cyan-400/20 shadow-[0_0_20px_rgba(34,211,238,0.08)] p-4 md:p-5">
      {title && (
        <h3 className="text-sm font-bold text-cyan-300 mb-4">
          {title}
        </h3>
      )}
      {renderChart()}
    </div>
  );
};

export default ChartBlock;
